import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { getLocalizedBlogPosts } from "../data/blogMultilingual";
import { SOLUTION_LANDINGS } from "../data/solutionLandings";
import { useLanguage } from "../contexts/LanguageContext";
import { useDbBlogPosts } from "../hooks/useDbBlogPosts";
import { localizedPath } from "../lib/locale";

const SITE_URL = "https://adrise-agency.com";

export default function BlogIndexPage() {
    const { language } = useLanguage();
    const dbPosts = useDbBlogPosts(language);
    const posts = [...dbPosts, ...getLocalizedBlogPosts(language)];
    const featured = posts[0];
    const rest = posts.slice(1);
    const c = {
        en: { eyebrow: "Resources & Insights", title1: "Playbooks for advertisers who refuse", title2: "to be throttled.", body: "Hands-on intelligence on agency ad accounts, HIVA tier mechanics, Credit Line funding, scaling playbooks, and compliance reality across major platforms.", featured: "Featured", read: "Read playbook" },
        pt: { eyebrow: "Recursos & Insights", title1: "Playbooks para anunciantes que se recusam", title2: "a travar no crescimento.", body: "Inteligência prática sobre contas de agência, mecânica HIVA, funding via Credit Line e compliance nas principais plataformas.", featured: "Destaque", read: "Ler playbook" },
        es: { eyebrow: "Recursos & Insights", title1: "Playbooks para anunciantes que no aceptan", title2: "quedarse bloqueados.", body: "Inteligencia práctica sobre cuentas de agencia, niveles HIVA, funding con Credit Line y compliance en las plataformas clave.", featured: "Destacado", read: "Leer playbook" },
        ru: { eyebrow: "Ресурсы и инсайты", title1: "Плейбуки для рекламодателей, которые не готовы", title2: "упираться в потолок.", body: "Практические материалы по агентским аккаунтам, уровням HIVA, Credit Line и compliance по ключевым платформам.", featured: "Главное", read: "Читать плейбук" },
        tr: { eyebrow: "Kaynaklar & İçgörüler", title1: "Büyümesi engellenmeyi kabul etmeyen", title2: "reklamverenler için playbooklar.", body: "Ajans hesapları, HIVA katmanları, Credit Line fonlama ve platform bazlı compliance süreçleri hakkında sahadan net rehberler.", featured: "Öne Çıkan", read: "Playbook'u oku" },
    }[language] || {
        eyebrow: "Resources & Insights", title1: "Playbooks for advertisers who refuse", title2: "to be throttled.", body: "Hands-on intelligence on agency ad accounts, HIVA tier mechanics, Credit Line funding, scaling playbooks, and compliance reality across major platforms.", featured: "Featured", read: "Read playbook",
    };
    const dateLocale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : language === "ru" ? "ru-RU" : language === "tr" ? "tr-TR" : "en-US";

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Adrise Resources & Insights",
        url: `${SITE_URL}/blog`,
        publisher: {
            "@type": "Organization",
            name: "Adrise",
            url: SITE_URL,
        },
        blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headrise: p.title,
            description: p.description,
            datePublished: p.date,
            url: `${SITE_URL}/blog/${p.slug}`,
            image: p.cover,
            author: { "@type": "Organization", name: p.author },
        })),
    };

    return (
        <main
            data-testid="blog-page"
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title="Adrise Resources — Agency Ad Account Insights & Playbooks"
                description="In-depth playbooks on Meta, Google, TikTok, Snap, native ads. How HIVA Partner accounts work. Credit Line vs Credit Card funding. Casino & gray-hat compliance. Updated for 2026."
                canonical={`${SITE_URL}/blog`}
                keywords={[
                    "agency ad accounts blog",
                    "Meta agency accounts",
                    "Google Ads MCC",
                    "TikTok agency",
                    "Platinum HIVA",
                    "credit line ads",
                ]}
                jsonLd={jsonLd}
            />

            <Navbar />

            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
                <div className="absolute inset-0 noise pointer-events-none" />
                <div className="container-skala relative">
                    <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-4">
                        {c.eyebrow}
                    </p>
                    <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tight max-w-4xl leading-[1.02]">
                        {c.title1}{" "}
                        <span className="text-gradient-primary">
                            {c.title2}
                        </span>
                    </h1>
                    <p className="text-muted-foreground text-base md:text-lg max-w-2xl mt-6">
                        {c.body}
                    </p>
                </div>
            </section>

            {/* Featured + grid */}
            <section className="pb-20 md:pb-28">
                <div className="container-skala">
                    {/* Featured */}
                    <Link
                        to={localizedPath(language, `/blog/${featured.slug}`)}
                        data-testid={`blog-featured-${featured.slug}`}
                        className="group grid lg:grid-cols-12 gap-8 mb-16 bg-card border border-white/[0.07] hover:border-primary/40 rounded-3xl p-6 md:p-8 transition-colors overflow-hidden"
                    >
                        <div className="lg:col-span-6 relative rounded-2xl overflow-hidden aspect-[16/10] bg-white/5">
                            <img
                                src={featured.cover}
                                alt={featured.title}
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                        </div>
                        <div className="lg:col-span-6 flex flex-col justify-center space-y-4">
                            <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em]">
                                <span className="text-primary">
                                    {c.featured} · {featured.category}
                                </span>
                                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(featured.date).toLocaleDateString(
                                        dateLocale,
                                        {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        },
                                    )}
                                </span>
                                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                                    <Clock className="w-3 h-3" />
                                    {featured.reading_time}
                                </span>
                            </div>
                            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight">
                                {featured.title}
                            </h2>
                            <p className="text-muted-foreground text-base leading-relaxed">
                                {featured.excerpt}
                            </p>
                            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                                {c.read}
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>

                    {/* Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {rest.map((p) => (
                            <Link
                                key={p.slug}
                                to={localizedPath(language, `/blog/${p.slug}`)}
                                data-testid={`blog-card-${p.slug}`}
                                className="group bg-card border border-white/[0.07] hover:border-primary/40 rounded-2xl overflow-hidden transition-colors flex flex-col"
                            >
                                <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                                    <img
                                        src={p.cover}
                                        alt={p.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-6 space-y-3 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em]">
                                        <span className="text-primary">
                                            {p.category}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {p.reading_time}
                                        </span>
                                    </div>
                                    <h3 className="font-display font-semibold text-xl leading-snug group-hover:text-primary transition-colors flex-1">
                                        {p.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                        {p.excerpt}
                                    </p>
                                    <div className="pt-2 text-xs text-muted-foreground font-mono inline-flex items-center gap-1.5">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(p.date).toLocaleDateString(
                                            dateLocale,
                                            {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                            },
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section
                data-testid="blog-seo-cluster-links"
                className="pb-14 md:pb-16"
            >
                <div className="container-skala">
                    <div className="rounded-2xl border border-white/[0.08] bg-card p-5 md:p-6">
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-3">
                            {({ tr: "Yüksek Niyetli Çözüm Sayfaları", es: "Páginas de soluciones de alta intención", pt: "Páginas de soluções de alta intenção", ru: "Страницы решений с высоким интентом" })[language] || "High-Intent Solution Pages"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                "facebook-ad-account-disabled-recovery",
                                "high-spend-google-ads-accounts",
                                "agency-ad-account-vs-personal",
                                "agency-ad-account-pricing-guide",
                                "daily-spend-limit-solution",
                            ].map((slug) => (
                                <Link
                                    key={slug}
                                    to={`/solutions/${slug}`}
                                    data-testid={`blog-cluster-link-${slug}`}
                                    className="text-xs md:text-sm px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-primary/40 hover:text-primary transition-colors"
                                >
                                    {SOLUTION_LANDINGS[slug]?.title?.[language] || SOLUTION_LANDINGS[slug]?.title?.en || slug.replaceAll("-", " ")}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
